import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  FaApple,
  FaChevronDown,
  FaChevronLeft,
  FaChevronRight,
  FaCopy,
  FaFacebook,
  FaHeart,
  FaLinkedin,
  FaShare,
  FaShoppingCart,
  FaStar,
  FaTelegram,
  FaTimes,
  FaVolumeMute,
  FaVolumeUp,
  FaWhatsapp,
  FaWindows,
} from "react-icons/fa";
import { TiArrowBack } from "react-icons/ti";
import { useDispatch, useSelector } from "react-redux";
import { getGameById } from "../Redux/Slice/game.slice";
import {
  addToCart as addToCartAction,
  fetchCart,
  setClearCart,
} from "../Redux/Slice/cart.slice";
import {
  addToWishlist,
  removeFromWishlist,
  checkWishlistStatus,
} from "../Redux/Slice/wishlist.slice";
import { DiAndroid } from "react-icons/di";
import { FaXTwitter } from "react-icons/fa6";
import { BsSend } from "react-icons/bs";
import { IoClose, IoGameController } from "react-icons/io5";
import {
  createrating,
  deleterating,
  getGameRatings,
  updaterating,
} from "../Redux/Slice/rating.slice";
import { Formik, useFormik } from "formik";
import * as Yup from "yup";
import { decryptData } from "../Utils/encryption";
import { RiDeleteBin6Fill, RiEdit2Fill } from "react-icons/ri";
import { Box, Modal } from "@mui/material";
import { createOrder, verifyPayment } from "../Redux/Slice/Payment.slice";
import GameDetailsSkeleton from "../component/skeleton-loaders/GameDetailsSkeleton";
import ReviewsSkeleton from "../component/skeleton-loaders/ReviewsSkeleton";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import StripeCheckoutForm from "../component/StripeCheckoutForm";
import OrderSuccessModal from "../component/OrderSuccessModal";
import axiosInstance from "../Utils/axiosInstance";
import { useSnackbar } from "notistack";
import { generateInvoice } from "../Utils/invoiceGenerator";

// Initialize Stripe with your publishable key
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

// Utility function to format date as relative time
const formatRelativeTime = (dateString) => {
  const now = new Date();
  const reviewDate = new Date(dateString);
  const diffTime = now - reviewDate;
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {
    return "Today";
  } else if (diffDays === 1) {
    return "Yesterday";
  } else if (diffDays < 7) {
    return `${diffDays} day${diffDays > 1 ? "s" : ""} ago`;
  } else if (diffDays < 30) {
    const weeks = Math.floor(diffDays / 7);
    return `${weeks} week${weeks > 1 ? "s" : ""} ago`;
  } else if (diffDays < 365) {
    const months = Math.floor(diffDays / 30);
    return `${months} month${months > 1 ? "s" : ""} ago`;
  } else {
    const years = Math.floor(diffDays / 365);
    return `${years} year${years > 1 ? "s" : ""} ago`;
  }
};

// Custom Share Modal Component
const ShareModal = ({ isOpen, onClose, singleGame, currentUrl }) => {
  const [copied, setCopied] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  // Handle modal opening animation
  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
    }
  }, [isOpen]);

  // Handle ESC key press to close modal
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === "Escape" && isOpen) {
        handleClose();
      }
    };

    // Add event listener when modal is open
    if (isOpen) {
      document.addEventListener("keydown", handleEscKey);
    }

    // Cleanup event listener
    return () => {
      document.removeEventListener("keydown", handleEscKey);
    };
  }, [isOpen]);

  // Handle modal closing with animation
  const handleClose = () => {
    setIsAnimating(false);
    setTimeout(() => {
      onClose();
    }, 200); // Match the transition duration
  };

  if (!isOpen) return null;

  const shareData = {
    title: singleGame?.title || "Check out this Game",
    description: singleGame?.description || "Amazing Game",
    url: currentUrl,
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(currentUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy link");
    }
  };

  const shareOptions = [
    {
      name: "Facebook",
      icon: FaFacebook,
      color: "bg-blue-600 hover:bg-blue-700",
      url: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
        currentUrl
      )}`,
    },
    {
      name: "Twitter",
      icon: FaXTwitter,
      color: "bg-sky-500 hover:bg-sky-600",
      url: `https://twitter.com/intent/tweet?url=${encodeURIComponent(
        currentUrl
      )}&text=${encodeURIComponent(shareData.title)}`,
    },
    {
      name: "WhatsApp",
      icon: FaWhatsapp,
      color: "bg-green-600 hover:bg-green-700",
      url: `https://wa.me/?text=${encodeURIComponent(
        shareData.title + " " + currentUrl
      )}`,
    },
    {
      name: "LinkedIn",
      icon: FaLinkedin,
      color: "bg-blue-700 hover:bg-blue-800",
      url: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
        currentUrl
      )}`,
    },
    {
      name: "Telegram",
      icon: FaTelegram,
      color: "bg-blue-500 hover:bg-blue-600",
      url: `https://t.me/share/url?url=${encodeURIComponent(
        currentUrl
      )}&text=${encodeURIComponent(shareData.title)}`,
    },
  ];

  const handleSocialShare = (url) => {
    window.open(url, "_blank", "width=600,height=400");
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  return (
    <div
      className={`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 transition-all duration-200 ease-in-out ${isAnimating
        ? "bg-black bg-opacity-50 backdrop-blur-sm"
        : "bg-black bg-opacity-0 backdrop-blur-none"
        }`}
      onClick={handleBackdropClick}
    >
      <div
        className={`backdrop-blur-sm rounded-2xl shadow-2xl w-full max-w-md mx-auto transition-all duration-200 ease-in-out transform ${isAnimating
          ? "scale-100 opacity-100 translate-y-0"
          : "scale-95 opacity-0 translate-y-4"
          }`}
      >
        {/* Modal Header */}
        <div className="flex items-center justify-between p-3 md:px-6 md:py-4 border-b border-white/50">
          <h2 className="text-base sm:text-xl font-bold text-white tracking-wider">
            Share Game
          </h2>
          <button
            onClick={handleClose}
            className="text-white transition-colors"
          >
            <FaTimes className="text-base sm:text-xl" />
          </button>
        </div>

        {/* Modal Content */}
        <div className="p-3 sm:p-6">
          {/* Game Preview */}
          <div className="mb-6">
            <h3 className="font-semibold text-white mb-2">{shareData.title}</h3>
            <p className="text-white/50 text-sm line-clamp-2">
              {shareData.description}
            </p>
          </div>

          {/* Share Options */}
          <div className="space-y-4">
            <h4 className="font-medium text-white">Share on social media</h4>
            <div className="grid grid-cols-5 gap-3">
              {shareOptions.map((option) => (
                <button
                  key={option.name}
                  onClick={() => handleSocialShare(option.url)}
                  className={`${option.color} text-white p-2 sm:py-3 sm:px-4 rounded sm:rounded-lg font-medium transition-colors flex items-center justify-center gap-2`}
                >
                  <option.icon className="text-base sm:text-lg" />
                  {/* {option.name} */}
                </button>
              ))}
            </div>
          </div>

          {/* Copy Link */}
          <div className="mt-6 pt-4 border-t border-white/50">
            <h4 className="font-medium text-white mb-3">Or copy link</h4>
            <div className="flex gap-2">
              <input
                type="text"
                value={currentUrl}
                readOnly
                className="flex-1 px-3 py-2 border border-white rounded-lg text-sm text-white bg-transparent focus:outline-none"
              />
              <button
                onClick={handleCopyLink}
                className={`p-2 sm:px-4 sm:py-2 focus:outline-none rounded-lg font-medium tracking-wide transition-colors flex items-center gap-2 ${copied
                  ? "bg-green-500 text-black"
                  : "bg-transparent text-white border border-white rounded-lg"
                  }`}
              >
                <FaCopy size={14} />
                {copied ? "Copied!" : "Copy"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default function GameDetails() {
  const { enqueueSnackbar } = useSnackbar();
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [selectedTab, setSelectedTab] = useState("overview");
  const [selectedPlatform, setSelectedPlatform] = useState("");
  const [openPlatform, setOpenPlatform] = useState(null);
  const singleGame = useSelector((state) => state.game.singleGame);
  const { loadingStates } = useSelector((state) => state.game);
  const { loadingStates: ratingLoadingStates, reviews, ratingCounts, totalReviews, averageRating } =
    useSelector((state) => state.rating);
  const cartItems = useSelector((state) => state.cart?.items || []);
  const { wishlistStatus } = useSelector((state) => state.wishlist);
  const [delOpen, setDelOpen] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const userId = localStorage.getItem("yoyouserId");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isReviewModal, setIsReviewModal] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [hoverRating, setHoverRating] = useState(0);
  const [reviewData, setReviewData] = useState("");
  const [showVideo, setShowVideo] = useState(false);
  const [isMuted, setIsMuted] = useState(true); // Start muted
  const videoRef = useRef(null); // Ref for the video element
  const user = useSelector((state) => state.auth.user);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [showBuyNowModal, setShowBuyNowModal] = useState(false);
  const [openStripeModal, setOpenStripeModal] = useState(false);
  const [clientSecret, setClientSecret] = useState("");
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [orderData, setOrderData] = useState(null);

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  useEffect(() => {
    document.title = `${singleGame?.title || "Game"} • YOYO`;
    window.scrollTo(0, 0);

    if (singleGame?.video) {
      const timer = setTimeout(() => {
        setShowVideo(true);
      }, 3000); // Show video after 3 seconds
      return () => clearTimeout(timer); // Clear timeout on component unmount or if video_url changes
    }
  }, [singleGame]);

  // Handle keyboard navigation for images
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        closeModal();
        return; // Stop further processing for the Escape key
      }

      // Navigation (ArrowLeft/ArrowRight) should only occur if there are multiple images.
      if (singleGame?.images && singleGame?.images?.length > 1) {
        if (event.key === "ArrowLeft") {
          prevImage();
        } else if (event.key === "ArrowRight") {
          nextImage();
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [singleGame, currentImageIndex]); // Depend on singleGame and currentImageIndex to ensure correct state

  useEffect(() => {
    if (id) {
      dispatch(getGameRatings(id));
    }
  }, [dispatch, id]);

  const validationSchema = Yup.object().shape({
    rate: Yup.number().required("Rating is required").min(1).max(5),
    description: Yup.string().max(
      500,
      "Description must be 500 characters or less"
    ),
  });

  const closeReviewModal = () => {
    setIsClosing(true);
    setTimeout(() => {
      setReviewData("");
      setIsReviewModal(false);
      setIsClosing(false);
      setHoverRating(0);
      formik.resetForm();
    }, 300);
  };

  const handleDeleteOpen = (data) => {
    setDelOpen(true);
    setReviewData(data);
  };
  const handleDeleteClose = () => setDelOpen(false);
  const handleDeleterate = () => {
    dispatch(deleterating({ _id: reviewData._id }));
    setDelOpen(false);
    setReviewData("");
  };

  const handleOpen = (data) => {
    setIsReviewModal(true);
    setReviewData(data);
    if (data) {
      formik.setValues({
        rate: data.rate,
        description: data.description,
        gameId: id,
      });
    }
  };

  const openModal = (image) => {
    const index = singleGame.images.findIndex(img => img._id === image._id);
    setCurrentImageIndex(index);
    setSelectedImage(image);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedImage(null);
  };

  const nextImage = () => {
    const nextIndex = (currentImageIndex + 1) % singleGame.images.length;
    setCurrentImageIndex(nextIndex);
    setSelectedImage(singleGame.images[nextIndex]);
  };

  const prevImage = () => {
    const prevIndex = currentImageIndex === 0 ? singleGame.images.length - 1 : currentImageIndex - 1;
    setCurrentImageIndex(prevIndex);
    setSelectedImage(singleGame.images[prevIndex]);
  };



  const handleKeyDown = (e) => {
    if (e.key === 'Escape') closeModal();
    if (e.key === 'ArrowRight') nextImage();
    if (e.key === 'ArrowLeft') prevImage();
  };

  const formik = useFormik({
    initialValues: {
      rate: 0,
      description: "",
      gameId: id,
    },
    validationSchema: validationSchema,
    onSubmit: async (values, { resetForm }) => {
      try {
        const handleRatingSubmit = async (values) => {
          const action = reviewData
            ? updaterating({ _id: reviewData._id, ...values })
            : createrating(values);

          const response = await dispatch(action);
          if (response.payload.success) {
            resetForm();
            closeReviewModal();
            values.rate = 0;
          }
        };
        await handleRatingSubmit(values);
      } catch (error) {
        console.error("Error submitting rating:", error);
        alert("Failed to submit rating. Please try again.");
      }
    },
  });

  const { handleSubmit, setFieldValue, handleChange, values, errors, touched } =
    formik;

  const renderStars = () => {
    return [...Array(5)].map((_, index) => {
      const starValue = index + 1;
      const isActive = starValue <= (hoverRating || values.rate);

      return (
        <button
          key={index}
          type="button"
          onClick={() => {
            setFieldValue("rate", starValue);
          }}
          onMouseEnter={() => setHoverRating(starValue)}
          onMouseLeave={() => setHoverRating(0)}
          className={`p-1 transition-all duration-200 transform hover:scale-110 ${isActive ? "text-yellow-400" : "text-white/50"
            } hover:text-yellow-300 ${values.rate === starValue ? "star-bounce" : ""
            }`}
        // disabled={isSubmitting}
        >
          <FaStar
            size={32}
            fill={isActive ? "currentColor" : "currentColor"}
            className="drop-shadow-lg"
            stroke={isActive ? "currentColor" : "currentColor"}
            strokeWidth={isActive ? 0 : 1}
          />
        </button>
      );
    });
  };



  const getAvailablePlatforms = () => {
    const platforms = singleGame?.platforms;
    if (!platforms) return {};

    const availablePlatforms = {};

    // Check each platform and include only if available is true
    if (platforms.windows?.available) {
      availablePlatforms.windows = platforms.windows;
    }
    if (platforms.ios?.available) {
      availablePlatforms.ios = platforms.ios;
    }
    if (platforms.android?.available) {
      availablePlatforms.android = platforms.android;
    }

    return availablePlatforms;
  };

  // Check if a game is already in cart for a specific platform
  const isGameInCart = (gameId, platform) => {
    return cartItems.some(
      (item) =>
        String(item.game?._id || item.game) === String(gameId) &&
        item.platform === platform
    );
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    // Fetch cart items to check what's already added
    const userId = localStorage.getItem("yoyouserId");
    const token = localStorage.getItem("yoyoToken");
    if (userId && token) {
      dispatch(fetchCart());
      // Check wishlist status for this game
      if (id) {
        dispatch(checkWishlistStatus(id));
      }
    }
  }, [dispatch, id]);

  useEffect(() => {
    const availablePlatforms = getAvailablePlatforms();
    if (Object.keys(availablePlatforms).length > 0) {
      setOpenPlatform(Object.keys(availablePlatforms)[0]); // Set the first platform as open
    }
    if (!availablePlatforms[selectedPlatform]) {
      setSelectedPlatform(Object.keys(availablePlatforms)[0]);
    }
  }, [singleGame, selectedPlatform]);

  useEffect(() => {
    if (id) {
      dispatch(getGameById(id));
    }
  }, [id]);

  const togglePlatform = (platformKey) => {
    setOpenPlatform((prev) => (prev === platformKey ? null : platformKey)); // Close if the same platform is clicked
  };

  const toggleWishlist = () => {
    if (!userId) {
      navigate("/login");
      return;
    }

    const gameId = singleGame?._id;
    if (!gameId) return;

    const isInWishlist = wishlistStatus[gameId];
    if (isInWishlist) {
      dispatch(removeFromWishlist({ gameId }));
    } else {
      dispatch(addToWishlist({ gameId }));
    }
  };

  const handleCheckout = async (items) => {
    if (checkoutLoading) return;
    setCheckoutLoading(true)

    const subtotal = items.reduce((sum, it) => sum + it.price, 0);
    const tax = subtotal * 0.18;
    const discount = subtotal * 0.05;
    const totalAmount = subtotal + tax - discount;

    try {
      if (process.env.REACT_APP_PAYMENT_TYPE === "razorpay") {
        const response = await axiosInstance.post("/order/create", { amount: totalAmount });
        if (response.data.success) {
          const { razorpayOrder } = response.data;
          const options = {
            key: process.env.REACT_APP_RAZORPAY_KEY_ID,
            amount: razorpayOrder.amount,
            currency: razorpayOrder.currency,
            name: "Yoyo Game",
            description: "Game Purchase",
            order_id: razorpayOrder.id,
            handler: async function (paymentResponse) {
              try {
                const verifyRes = await axiosInstance.post("/order/verify", {
                  items,
                  amount: totalAmount,
                  paymentResponse,
                  buyNow: true
                });
                if (verifyRes.data.success || verifyRes.data.order) {
                  // Only clear cart if it's not a buy now transaction
                  // const buyNow = true;
                  // if (!buyNow) {
                  //   dispatch(setClearCart());
                  // }
                  enqueueSnackbar("Payment successful!", { variant: "success" });

                  // Generate invoice
                  try {
                    const updatedOrder = verifyRes.data.order;
                    const userDetails = {
                      name: user?.fullName ? decryptData(user.fullName) : "Customer",
                      email: user?.email ? decryptData(user.email) : "",
                    };
                    generateInvoice(updatedOrder, userDetails, "save");
                  } catch (e) {
                    console.error("Error auto-opening invoice", e);
                  }

                  setShowSuccessModal(true);
                }
              } catch (err) {
                console.error("Payment verification failed", err);
              }
            },
            prefill: {
              name: user?.fullName ? decryptData(user.fullName) : "Game User",
              email: user?.email ? decryptData(user.email) : "user@example.com",
            },
            theme: { color: "#7c3aed" },
          };
          const rzp = new window.Razorpay(options);
          rzp.open();
        }
      } else {
        // Create Stripe Payment Intent
        const response = await axiosInstance.post("/order/stripe/create-payment-intent", {
          amount: totalAmount,
        });

        if (response.data.success) {
          setClientSecret(response.data.clientSecret);
          setOrderData({ items, amount: totalAmount });
          setOpenStripeModal(true);
        }
      }
    } catch (err) {
      console.error("Error creating payment intent/order:", err);
    } finally {
      setCheckoutLoading(false);
    }
  };

  const handleCloseStripeModal = () => {
    setOpenStripeModal(false);
    setClientSecret("");
  };

  useEffect(() => {
    if (isModalOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isModalOpen]);

  return (
    <>
      {loadingStates.getGameById || !singleGame ? (
        <GameDetailsSkeleton />
      ) : (
        <div className="min-h-screen relative">
          {/* Hero Section */}
          <picture>
            <source srcSet={singleGame?.cover_image?.url} type="image/webp" />
            <source srcSet={singleGame?.cover_image?.url} type="image/avif" />
            <img
              src={singleGame?.cover_image?.url}
              alt="Game cover"
              className="fixed inset-0 w-full h-full object-cover -z-10"
            />
          </picture>
          <div className="absolute inset-0 backdrop-blur-md bg-gradient-to-r from-black/60 to-transparent z-10"></div>
          {/* Content will scroll over the background image */}
          <div className="mx-auto w-[95%] sm:w-[92%] md:w-[90%] lg:max-w-[80%] py-8 relative z-20">
            <div className="relative overflow-hidden mt-[50px] mb-5">
              {/* backdrop-blur-lg bg-black/40 rounded-full */}
              <TiArrowBack
                onClick={() => {
                  navigate(-1);
                }}
                className="absolute inset-0 z-30 text-white top-2 left-2 text-3xl 
                    [filter:drop-shadow(0_3px_2px_rgba(0,0,0,0.5))]"
              />
              {/* <img
                        src={singleGame?.cover_image?.url}
                        alt="Game Screenshot"
                        className="w-full h-96 object-cover transition-all duration-700 rounded-lg"
                    /> */}

              {showVideo && singleGame?.video ? (
                <>
                  <video
                    ref={videoRef}
                    src={singleGame?.video?.url}
                    autoPlay
                    loop
                    muted={isMuted}
                    className="w-full h-96 object-cover transition-all duration-700 rounded-lg"
                  />
                  <button
                    onClick={toggleMute}
                    className="absolute bottom-4 right-4 z-30 p-3 rounded-full bg-black/50 text-white hover:bg-black/70 transition-all duration-300"
                  >
                    {isMuted ? (
                      <FaVolumeMute className="w-5 h-5" />
                    ) : (
                      <FaVolumeUp className="w-5 h-5" />
                    )}
                  </button>
                </>
              ) : (
                <img
                  src={singleGame?.cover_image?.url}
                  alt="Game Screenshot"
                  className="w-full h-96 object-cover transition-all duration-700 rounded-lg"
                />
              )}

              {/* Game Title Overlay */}
              <div className="absolute inset-0 flex items-end">
                <div className="p-8 text-white">
                  <h1 className="text-2xl md:text-4xl font-bold mb-2 bg-gradient-to-r from-white to-purple-200 bg-clip-text text-transparent [filter:drop-shadow(0_3px_2px_rgba(0,0,0,0.5))] tracking-wide">
                    {singleGame?.title.replace(/"/g, "")}
                  </h1>
                  <p className="text-sm w-full md:w-[50%] text-gray-300 mb-4 line-clamp-3">
                    {singleGame?.description.replace(/"/g, "")}
                  </p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 xl:gap-8">
              {/* Main Content */}
              <div className="lg:col-span-2 space-y-4 md:space-y-8">
                {/* Action Buttons */}
                <div className="flex flex-wrap gap-4">
                  {selectedPlatform && singleGame?.platforms?.[selectedPlatform] ? (
                    isGameInCart(singleGame?._id, selectedPlatform) ? (
                      <button
                        disabled
                        className="bg-gray-500 text-white px-6 md:px-8 py-3 rounded-md font-semibold flex items-center space-x-2 transition-all duration-300 transform hover:scale-105 shadow-lg text-sm md:text-base cursor-not-allowed"
                      >
                        <FaShoppingCart className="w-5 h-5" />
                        <span>Already in Cart</span>
                      </button>
                    ) : (
                      <button
                        onClick={() => {
                          if (!userId) {
                            navigate("/login");
                            return;
                          }
                          dispatch(
                            addToCartAction({
                              gameId: singleGame?._id,
                              platform: selectedPlatform,
                              qty: 1,
                            })
                          );
                        }}
                        className="bg-gradient-primary text-white px-6 md:px-8 py-3 rounded-md font-semibold flex items-center space-x-2 transition-all duration-300 transform hover:scale-105 shadow-lg text-sm md:text-base"
                      >
                        <FaShoppingCart className="w-5 h-5" />
                        <span>Add to Cart</span>
                      </button>
                    )
                  ) : (
                    <button className="bg-gray-500 text-white px-6 md:px-8 py-3 rounded-md font-semibold flex items-center space-x-2 transition-all duration-300 transform hover:scale-105 shadow-lg text-sm md:text-base cursor-not-allowed">
                      <span>Select Platform</span>
                    </button>
                  )}

                  <button
                    onClick={toggleWishlist}
                    className={`px-3 md:px-6 py-3 rounded-md text-sm md:text-base font-semibold flex items-center space-x-2 transition-all duration-300 border ${wishlistStatus[singleGame?._id]
                      ? "bg-red-500 hover:bg-red-600 text-white border-red-500"
                      : "bg-transparent hover:bg-black/50 text-white border-white/40"
                      }`}
                  >
                    <FaHeart
                      className={`w-5 h-5 ${wishlistStatus[singleGame?._id] ? "fill-white" : ""
                        }`}
                    />
                    <span>
                      {wishlistStatus[singleGame?._id]
                        ? "Wishlisted"
                        : "Add to Wishlist"}
                    </span>
                  </button>

                  <button
                    className="bg-transparent hover:bg-black/50 text-white text-sm md:text-base px-3 md:px-6 py-3 rounded-md font-semibold flex items-center space-x-2 transition-all duration-300 border border-white/40"
                    onClick={() => {
                      setShowShareModal(true);
                    }}
                  >
                    <FaShare className="w-5 h-5" />
                    <span>Share</span>
                  </button>
                </div>

                <div className="bg-black/50 rounded-xl py-3 px-4 md:px-6 backdrop-blur-sm border border-white/40 block lg:hidden">
                  <nav className="flex space-x-4 mb-2">
                    {Object.keys(getAvailablePlatforms()).map((platform) => (
                      <button
                        key={platform}
                        onClick={() => setSelectedPlatform(platform)}
                        className={`p-2 border-b-2 font-medium text-2xl capitalize transition-all duration-300 ${selectedPlatform === platform
                          ? "border-purple-500 text-purple-400"
                          : "border-transparent text-white/50 hover:text-white"
                          }`}
                      >
                        {/* {platform} */}
                        {platform === "windows" && (
                          <FaWindows
                            title="Show Windows Price"
                            className={`${selectedPlatform === platform ? "text-blue-500" : ""
                              }`}
                          />
                        )}
                        {platform === "ios" && (
                          <FaApple
                            title="Show Ios Price"
                            className={`${selectedPlatform === platform ? "text-white" : ""
                              }`}
                          />
                        )}
                        {platform === "android" && (
                          <DiAndroid
                            title="Show Android Price"
                            className={`${selectedPlatform === platform ? "text-green-500" : ""
                              }`}
                          />
                        )}
                      </button>
                    ))}
                  </nav>

                  {selectedPlatform &&
                    singleGame?.platforms?.[selectedPlatform] && (
                      <div className="flex items-center justify-between">
                        <div className="text-sm md:text-base">
                          <p className="text-white">
                            Price : $ {singleGame.platforms[selectedPlatform].price}
                          </p>
                          <p className="text-white">
                            Size : {singleGame.platforms[selectedPlatform].size}
                          </p>
                        </div>
                        {isGameInCart(singleGame?._id, selectedPlatform) ? (
                          <button
                            disabled
                            className="bg-gray-500 text-white px-2 h-10 rounded-md font-semibold flex items-center space-x-2 transition-all duration-300 transform hover:scale-105 shadow-lg text-xs cursor-not-allowed"
                          >
                            <FaShoppingCart className="w-4 h-4" />
                            <span>Already Added</span>
                          </button>
                        ) : (
                          <button
                            onClick={() => {
                              if (!userId) {
                                navigate("/login");
                                return;
                              }
                              dispatch(
                                addToCartAction({
                                  gameId: singleGame?._id,
                                  platform: selectedPlatform,
                                  qty: 1,
                                })
                              );
                            }}
                            className="bg-gradient-primary text-white px-2 h-10 rounded-md font-semibold flex items-center space-x-2 transition-all duration-300 transform hover:scale-105 shadow-lg text-xs"
                          >
                            <FaShoppingCart className="w-4 h-4" />
                            <span>Add To Cart</span>
                          </button>
                        )}
                      </div>
                    )}
                </div>

                {/* Tabs */}
                <div className="border-b border-white/40">
                  <nav className="flex space-x-8">
                    {["overview", "requirements", "reviews"].map((tab) => (
                      <button
                        key={tab}
                        onClick={() => setSelectedTab(tab)}
                        className={`py-4 px-2 border-b-2 font-medium text-sm capitalize transition-all duration-300 ${selectedTab === tab
                          ? "border-purple-500 text-purple-400"
                          : "border-transparent text-white/80 hover:text-white"
                          }`}
                      >
                        {tab}
                      </button>
                    ))}
                  </nav>
                </div>

                {/* Tab Content */}
                <div className="bg-black/50 rounded-xl p-4 md:p-6 backdrop-blur-sm border border-white/40">
                  {selectedTab === "overview" && (
                    <div className="space-y-2 md:space-y-6">
                      <div>
                        <h3 className="text-xl md:text-2xl font-bold text-white mb-2 md:mb-4">
                          About This Game
                        </h3>
                        <p className="text-gray-300 leading-relaxed text-sm md:text-base">
                          {singleGame?.description.replace(/"/g, "")}
                        </p>
                      </div>
                      <div>
                        <h4 className="text-base md:text-xl font-semibold text-white mb-1 md:mb-2">
                          Tags
                        </h4>
                        <div className="flex flex-wrap gap-2 mb-4">
                          {singleGame?.tags?.slice(0, 3).map((tag) => (
                            <span
                              key={tag}
                              className="px-3 py-1 mt-2 bg-gradient-to-r from-purple-500/20 to-pink-500/20 text-purple-200 text-xs rounded-full border border-purple-400/30"
                            >
                              {tag.replace(/"/g, "")}
                            </span>
                          ))}
                        </div>
                      </div>

                      <div>
                        <h4 className="text-base md:text-xl font-semibold text-white mb-1 md:mb-3">
                          Key Features
                        </h4>
                        <ul className="space-y-2 text-gray-300 text-sm md:text-base">
                          {singleGame?.instructions?.length === 0 && (
                            <li className="flex items-start space-x-3">
                              <div className="w-2 h-2 bg-purple-500 rounded-full mt-2"></div>
                              <span>No Key Features Found</span>
                            </li>
                          )}
                          {singleGame?.instructions?.map((ins) => (
                            <li key={ins} className="flex items-start space-x-3">
                              <div className="w-2 h-2 bg-purple-500 rounded-full mt-2"></div>
                              <span>{ins}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}

                  {selectedTab === "requirements" && (
                    <div className="space-y-2">
                      {Object.keys(getAvailablePlatforms()).map((platformKey) => {
                        const platform = getAvailablePlatforms()[platformKey];
                        if (platform.available) {
                          return (
                            <div
                              key={platformKey}
                              className="border border-white/40 rounded-lg overflow-hidden"
                            >
                              <button
                                onClick={() => togglePlatform(platformKey)}
                                className="w-full px-4 py-3 text-left flex justify-between items-center hover:bg-black/30 focus:outline-none transition-colors duration-200"
                                aria-expanded={openPlatform === platformKey}
                                aria-controls={`platform-content-${platformKey}`}
                              >
                                <h3 className="text-lg font-medium text-white">
                                  {platformKey.charAt(0).toUpperCase() +
                                    platformKey.slice(1)}
                                </h3>
                                <FaChevronDown
                                  className={`w-5 h-5 text-white transform transition-transform duration-200 ${openPlatform === platformKey ? "rotate-180" : ""
                                    }`}
                                />
                              </button>

                              <div
                                id={`platform-content-${platformKey}`}
                                className={`overflow-hidden transition-all duration-300 ease-in-out ${openPlatform === platformKey
                                  ? "max-h-96 opacity-100"
                                  : "max-h-0 opacity-0"
                                  }`}
                              >
                                <div className="px-4 pb-4 pt-2 border-t border-white/40">
                                  {platform.system_requirements && (
                                    <ul className="space-y-2">
                                      {Object.entries(
                                        platform.system_requirements
                                      ).map(([key, value]) => (
                                        <li key={key} className="text-white/50">
                                          <strong className="text-white tracking-wider">
                                            {key} :
                                          </strong>{" "}
                                          {value}
                                        </li>
                                      ))}
                                    </ul>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        }
                        return null;
                      })}
                    </div>
                  )}

                  {selectedTab === "reviews" && (
                    <>
                      {ratingLoadingStates.getGameRatings ? (
                        <ReviewsSkeleton />
                      ) : (
                        <div className="space-y-6">
                          <div className="flex justify-between items-center">
                            <h3 className="text-2xl font-bold text-white mb-4">
                              Reviews
                            </h3>
                            <button
                              onClick={() => {
                                setIsReviewModal(true);
                              }}
                              className="bg-transparent hover:bg-black/50 text-sm md:text-base p-3 rounded-md font-semibold flex items-center space-x-2 transition-all duration-300 border border-white/40 text-white mb-4 tracking-wide"
                            >
                              + Add Review
                            </button>
                          </div>

                          {/* Rating Statistics */}
                          {(averageRating > 0 ||
                            totalReviews > 0 ||
                            ratingCounts > 0) && (
                              <div className="bg-gray-900/30 rounded-xl p-6 border border-gray-600">
                                <div className="flex items-center space-x-6">
                                  <div className="text-center">
                                    <div className="text-3xl font-bold text-white">
                                      {averageRating || 0}
                                    </div>
                                    <div className="flex items-center justify-center space-x-1 mt-1">
                                      {[1, 2, 3, 4, 5].map((star) => (
                                        <FaStar
                                          key={star}
                                          className={`w-4 h-4 ${star <= (averageRating || 0)
                                            ? "fill-yellow-400 text-yellow-400"
                                            : "text-gray-600"
                                            }`}
                                        />
                                      ))}
                                    </div>
                                    <div className="text-gray-400 text-sm mt-1">
                                      {totalReviews || 0} reviews
                                    </div>
                                  </div>

                                  <div className="flex-1">
                                    {[5, 4, 3, 2, 1].map((star) => (
                                      <div
                                        key={star}
                                        className="flex items-center space-x-2 mb-1"
                                      >
                                        <span className="text-white text-sm w-4">
                                          {star}
                                        </span>
                                        <div className="flex-1 bg-gray-700 rounded-full h-2">
                                          <div
                                            className="bg-yellow-400 h-2 rounded-full"
                                            style={{
                                              width: `${totalReviews > 0
                                                ? ((ratingCounts[star] || 0) /
                                                  totalReviews) *
                                                100
                                                : 0
                                                }%`,
                                            }}
                                          ></div>
                                        </div>
                                        <span className="text-gray-400 text-sm w-8 text-right">
                                          {ratingCounts[star] || 0}
                                        </span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              </div>
                            )}
                          <div className="space-y-4">
                            {reviews.length > 0 ? (
                              reviews.map((review, index) => (
                                <div
                                  key={index}
                                  className="bg-gray-900/50 rounded-xl p-4 border border-gray-600"
                                >
                                  <div className="flex items-center justify-between mb-2">
                                    <div className="flex items-center space-x-3">
                                      {review.userImage ? (
                                        <img
                                          src={review.userImage}
                                          alt={decryptData(review.userName)}
                                          className="w-8 h-8 rounded-full object-cover"
                                        />
                                      ) : (
                                        <div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center text-white text-sm font-bold">
                                          {decryptData(review.userName)
                                            ? decryptData(review.userName[0])
                                            : "U"}
                                        </div>
                                      )}
                                      <span className="text-white font-semibold">
                                        {decryptData(review.userName) || "Anonymous"}
                                      </span>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                      <span className="text-gray-400 text-sm">
                                        {formatRelativeTime(review.createdAt)}
                                      </span>
                                      {/* Show edit button only if the review belongs to the current user */}
                                      {userId && review.userId === userId && (
                                        <>
                                          <button
                                            onClick={() => handleOpen(review)}
                                            className="text-blue-500 hover:text-blue-600 transition-colors duration-200"
                                            title="Edit your review"
                                          >
                                            <RiEdit2Fill />
                                          </button>
                                          <button
                                            onClick={() => handleDeleteOpen(review)}
                                            className="text-red-500 hover:text-red-600 transition-colors duration-200"
                                            title="Edit your review"
                                          >
                                            <RiDeleteBin6Fill />
                                          </button>
                                        </>
                                      )}
                                    </div>
                                  </div>
                                  <div className="flex items-center space-x-2 mb-2">
                                    {[1, 2, 3, 4, 5].map((star) => (
                                      <FaStar
                                        key={star}
                                        className={`w-4 h-4 ${star <= review.rate
                                          ? "fill-yellow-400 text-yellow-400"
                                          : "text-gray-600"
                                          }`}
                                      />
                                    ))}
                                  </div>
                                  <p className="text-gray-300">{review.description}</p>
                                </div>
                              ))
                            ) : (
                              <div className="text-center py-8">
                                <p className="text-gray-400">
                                  No reviews yet. Be the first to review this game!
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>

              {/* Sidebar */}
              <div className="space-y-6">
                {/* platforms Tabs and Tab Content */}
                <div className="bg-black/50 rounded-xl py-3 px-3 xl:px-6 backdrop-blur-sm border border-white/40 hidden lg:block">
                  <nav className="flex space-x-4 mb-2">
                    {Object.keys(getAvailablePlatforms()).map((platform) => (
                      <button
                        key={platform}
                        onClick={() => setSelectedPlatform(platform)}
                        className={`p-2 border-b-2 font-medium text-2xl capitalize transition-all duration-300 ${selectedPlatform === platform
                          ? "border-purple-500 text-purple-400"
                          : "border-transparent text-white/50 hover:text-white"
                          }`}
                      >
                        {/* {platform} */}
                        {platform === "windows" && (
                          <FaWindows
                            title="Show Windows Price"
                            className={`${selectedPlatform === platform ? "text-blue-500" : ""
                              }`}
                          />
                        )}
                        {platform === "ios" && (
                          <FaApple
                            title="Show Ios Price"
                            className={`${selectedPlatform === platform ? "text-white" : ""
                              }`}
                          />
                        )}
                        {platform === "android" && (
                          <DiAndroid
                            title="Show Android Price"
                            className={`${selectedPlatform === platform ? "text-green-500" : ""
                              }`}
                          />
                        )}
                      </button>
                    ))}
                  </nav>

                  {selectedPlatform &&
                    singleGame?.platforms?.[selectedPlatform] && (
                      <div className="flex items-center justify-between">
                        <div className="text-sm md:text-base">
                          <p className="text-white">
                            Price : $ {singleGame.platforms[selectedPlatform].price}
                          </p>
                          <p className="text-white">
                            Size : {singleGame.platforms[selectedPlatform].size}
                          </p>
                        </div>
                        {isGameInCart(singleGame._id, selectedPlatform) ? (
                          <button
                            disabled
                            className="bg-gray-500 text-white px-2 h-10 rounded-md font-semibold flex items-center space-x-2 text-xs cursor-not-allowed"
                          >
                            <FaShoppingCart className="w-4 h-4" />
                            <span>Already in Cart</span>
                          </button>
                        ) : !userId ? (
                          <button
                            onClick={() => navigate("/login")}
                            className="bg-gradient-primary text-white px-2 h-10 rounded-md font-semibold flex items-center space-x-2 transition-all duration-300 transform hover:scale-105 shadow-lg text-xs"
                          >
                            <FaShoppingCart className="w-4 h-4" />
                            <span>Login to Add</span>
                          </button>
                        ) : (
                          <button
                            onClick={() => setShowBuyNowModal(true)}
                            disabled={checkoutLoading}
                            className="bg-gradient-primary text-white px-2 h-10 rounded-md font-semibold flex items-center space-x-2 transition-all duration-300 transform hover:scale-105 shadow-lg text-xs"
                          >
                            {checkoutLoading ? "Processing..." :
                              <>
                                <FaShoppingCart className="w-4 h-4" />
                                <span>Buy Now</span>
                              </>
                            }

                          </button>
                        )}
                      </div>
                    )}
                </div>

                {/* Game Images */}
                {/* <div className="bg-black/50 rounded-xl p-3 backdrop-blur-sm border border-white/40">
                            <div className="grid grid-cols-2 md600:grid-cols-4 lg:grid-cols-2 xl:grid-cols-3 gap-4">
                                {singleGame?.images.map((image) => (
                                    <img
                                        key={image?._id}
                                        src={image?.url}
                                        alt={`Image of ${singleGame?.title}`}
                                        className="rounded-lg w-[165px] h-[220px] mx-auto object-cover" // Add any additional styling you need
                                        onClick={() => openModal(image)} // Open modal on click
                                    />
                                ))}
                            </div>
                        </div> */}
                <div className="bg-black/30 rounded-2xl p-6 backdrop-blur-sm border border-white/20">
                  <div className="columns-1 sm:columns-2 lg:columns-3 xl:columns-4 gap-4 space-y-4">
                    {!singleGame?.images || singleGame.images.length === 0 ? (
                      // Show skeleton for images when no images available
                      Array.from({ length: 8 }, (_, index) => (
                        <div key={index} className="break-inside-avoid mb-4">
                          <div className="relative overflow-hidden rounded-lg bg-gray-800 shadow-lg">
                            <div className="w-full h-32 bg-gray-600/50 animate-pulse"></div>
                          </div>
                        </div>
                      ))
                    ) : (
                      singleGame?.images.map((image, index) => (
                        <div
                          key={image?._id}
                          className="break-inside-avoid mb-4 group cursor-none"
                          onClick={() => openModal(image)}
                        >
                          <div className="relative overflow-hidden rounded-lg bg-gray-800 shadow-lg transition-all duration-300 hover:shadow-2xl hover:scale-[1.02]">
                            <img
                              src={image?.url}
                              alt={`Image ${index + 1} of ${singleGame?.title}`}
                              className="w-full h-auto object-cover transition-transform duration-300 group-hover:scale-110"
                              loading="lazy"
                            />
                            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300"></div>
                            <div className="absolute top-2 right-2 bg-black/50 text-white px-2 py-1 rounded text-sm opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                              {index + 1}
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>

                {/* Achievements Preview */}
                {/* <div className="bg-gray-800/50 rounded-xl p-6 border border-white/40">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-xl font-bold text-white">Achievements</h3>
                                <FaTrophy className="w-6 h-6 text-yellow-500" />
                            </div>
                            <div className="space-y-3">
                                <div className="flex items-center space-x-3">
                                    <div className="w-10 h-10 bg-gradient-to-r from-yellow-500 to-orange-500 rounded-lg flex items-center justify-center">
                                        <FaTrophy className="w-5 h-5 text-white" />
                                    </div>
                                    <div>
                                        <div className="text-white font-semibold text-sm">First Steps</div>
                                        <div className="text-gray-400 text-xs">Complete the tutorial</div>
                                    </div>
                                </div>
                                <div className="flex items-center space-x-3">
                                    <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg flex items-center justify-center">
                                        <FaStar className="w-5 h-5 text-white" />
                                    </div>
                                    <div>
                                        <div className="text-white font-semibold text-sm">Cyber Legend</div>
                                        <div className="text-gray-400 text-xs">Reach max level</div>
                                    </div>
                                </div>
                                <button className="w-full text-purple-400 hover:text-purple-300 text-sm transition-colors duration-300">
                                    View all 48 achievements →
                                </button>
                            </div>
                        </div> */}
              </div>

              {/* Buy Now Modal */}
              <Modal
                open={showBuyNowModal}
                onClose={() => setShowBuyNowModal(false)}
                aria-labelledby="buy-now-modal"
                aria-describedby="buy-now-description"
                className="cursor-none"
              >
                <Box className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-full max-w-md bg-gray-900 border border-white/20 rounded-2xl shadow-2xl outline-none overflow-hidden hover:outline-none focus:outline-none cursor-none">
                  {/* Glow effects */}
                  <div className="absolute top-0 left-1/4 w-32 h-32 bg-purple-600/30 rounded-full blur-[50px] pointer-events-none"></div>
                  <div className="absolute bottom-0 right-1/4 w-32 h-32 bg-blue-600/30 rounded-full blur-[50px] pointer-events-none"></div>

                  <div className="relative z-10 p-6">
                    <div className="flex justify-between items-center mb-6">
                      <h2 className="text-2xl font-bold text-white flex items-center gap-2 cursor-none">
                        <FaShoppingCart className="text-purple-400 cursor-none" />
                        Checkout
                      </h2>
                      <button
                        onClick={() => setShowBuyNowModal(false)}
                        className="text-gray-400 hover:text-white transition-colors cursor-none"
                      >
                        <IoClose size={24} />
                      </button>
                    </div>

                    {/* Item Details */}
                    <div className="bg-white/5 rounded-xl p-4 mb-6 border border-white/10">
                      <h3 className="text-lg font-semibold text-white mb-1 truncate">{singleGame?.title}</h3>
                      <p className="text-white/60 text-sm capitalize mb-4">Platform: <span className="text-purple-300">{selectedPlatform}</span></p>

                      <div className="space-y-3 text-sm">
                        <div className="flex justify-between text-gray-300">
                          <span>Price</span>
                          <span>${Number(singleGame?.platforms?.[selectedPlatform]?.price || 0).toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between text-gray-300">
                          <span>Tax (18%)</span>
                          <span>+${(Number(singleGame?.platforms?.[selectedPlatform]?.price || 0) * 0.18).toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between text-green-400">
                          <span>Discount (5%)</span>
                          <span>-${(Number(singleGame?.platforms?.[selectedPlatform]?.price || 0) * 0.05).toFixed(2)}</span>
                        </div>

                        <div className="border-t border-white/10 pt-3 flex justify-between text-white font-bold text-lg">
                          <span>Total</span>
                          <span>${(Number(singleGame?.platforms?.[selectedPlatform]?.price || 0) * 1.13).toFixed(2)}</span>
                        </div>
                      </div>
                    </div>

                    <div className="flex gap-4">
                      <button
                        onClick={() => setShowBuyNowModal(false)}
                        className=" cursor-none flex-1 py-3 rounded-xl bg-gray-700/50 hover:bg-gray-700 text-white font-semibold transition-all border border-white/10"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={() => {
                          handleCheckout([
                            {
                              game: singleGame._id,
                              platform: selectedPlatform,
                              price: Number(
                                singleGame.platforms[selectedPlatform]?.price ||
                                0
                              ),
                              name: singleGame.title,
                            },
                          ]);
                          setShowBuyNowModal(false);
                        }}
                        disabled={checkoutLoading}
                        className=" cursor-none flex-1 py-3 rounded-xl bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-500 hover:to-blue-500 text-white font-bold shadow-lg shadow-purple-500/25 transition-all flex items-center justify-center gap-2 transform hover:scale-[1.02]"
                      >
                        {checkoutLoading ? "Processing..." : "Pay Now"}
                      </button>
                    </div>
                  </div>
                </Box>
              </Modal>

              {/* Stripe Payment Modal */}
              <Modal
                open={openStripeModal}
                onClose={handleCloseStripeModal}
                aria-labelledby="stripe-checkout-modal"
                aria-describedby="stripe-payment-form"
              >
                <Box
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: { xs: '90%', sm: '500px' },
                    maxHeight: '90vh',
                    overflowY: 'auto',
                    bgcolor: 'rgba(15, 15, 35, 0.98)',
                    border: '1px solid rgba(124, 58, 237, 0.3)',
                    borderRadius: '16px',
                    boxShadow: '0 8px 32px rgba(124, 58, 237, 0.2)',
                    p: 4,
                  }}
                >
                  <div className="text-white">
                    <h2 className="text-2xl font-bold mb-4 bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
                      Complete Your Payment
                    </h2>
                    {clientSecret && orderData && (
                      <Elements stripe={stripePromise} options={{ clientSecret }}>
                        <StripeCheckoutForm
                          clientSecret={clientSecret}
                          orderData={orderData}
                          buyNow={true}
                          onSuccess={() => {
                            handleCloseStripeModal();
                            setShowSuccessModal(true);
                          }}
                          onCancel={handleCloseStripeModal}
                        />
                      </Elements>
                    )}
                  </div>
                </Box>
              </Modal>

              {/* Image Modal */}
              {isModalOpen && (
                <div
                  className="fixed inset-0 bg-black/90 z-[99999] flex items-center justify-center p-4 mt-0"
                  onClick={closeModal}
                  onKeyDown={handleKeyDown}
                >
                  <div className=" flex items-center justify-center max-w-5xl w-full max-h-full h-full mx-auto">
                    {/* Close Button */}
                    <button
                      onClick={closeModal}
                      className="absolute top-12 right-12 text-white hover:text-gray-300 transition-colors z-10"
                    >
                      <IoClose size={32} />
                    </button>

                    {/* Navigation Buttons */}
                    {singleGame.images.length > 1 && (
                      <>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            prevImage();
                          }}
                          className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-colors z-10 cursor-none"
                        >
                          <FaChevronLeft size={20} />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            nextImage();
                          }}
                          className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-colors z-10 cursor-none"
                        >
                          <FaChevronRight size={20} />
                        </button>
                      </>
                    )}

                    {/* Centered Image */}
                    <div className="flex items-center justify-center w-full h-full">
                      <img
                        src={selectedImage?.url}
                        alt={`Full size image of ${singleGame?.title}`}
                        className="max-w-[90%] max-h-[90%] object-contain rounded-lg mx-auto my-auto"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>

                    {/* Image Counter */}
                    <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/50 text-white px-3 py-1 rounded-full text-sm">
                      {currentImageIndex + 1} of {singleGame.images.length}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
          {/* {isModalOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-30">
          <div className="p-4 rounded-lg relative z-30">
            <div className="relative max-w-[900px] max-h-[450px]">
              <button
                onClick={closeModal}
                className="absolute top-6 right-6 text-white text-2xl [filter:drop-shadow(0_3px_2px_rgba(0,0,0,0.5))]"
              >
                <FaTimes />
              </button>
              <img
                src={selectedImage?.url}
                alt={`Selected image of ${singleGame?.title}`}
                className="w-full h-full rounded-md object-contain"
              />
            </div>
          </div>
        </div>
      )} */}

          <style jsx="true">{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes fadeOut {
          from {
            opacity: 1;
          }
          to {
            opacity: 0;
          }
        }

        @keyframes slideIn {
          from {
            opacity: 0;
            transform: scale(0.8) translateY(-30px);
          }
          to {
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }

        @keyframes slideOut {
          from {
            opacity: 1;
            transform: scale(1) translateY(0);
          }
          to {
            opacity: 0;
            transform: scale(0.8) translateY(-30px);
          }
        }

        @keyframes bounce {
          0%,
          20%,
          53%,
          80%,
          100% {
            transform: translate3d(0, 0, 0);
          }
          40%,
          43% {
            transform: translate3d(0, -8px, 0);
          }
          70% {
            transform: translate3d(0, -4px, 0);
          }
          90% {
            transform: translate3d(0, -2px, 0);
          }
        }

        .modal-enter {
          animation: fadeIn 0.3s ease-out;
        }

        .modal-exit {
          animation: fadeOut 0.3s ease-in;
        }

        .modal-content-enter {
          animation: slideIn 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
        }

        .modal-content-exit {
          animation: slideOut 0.3s ease-in;
        }

        .star-bounce {
          animation: bounce 0.6s;
        }
      `}</style>

          {isReviewModal && (
            <div
              className={`fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-30 p-4 backdrop-blur-sm ${isClosing ? "modal-exit" : "modal-enter"
                }`}
            >
              <div
                className={`bg-white/15 backdrop-blur-xl rounded-md shadow-2xl w-full max-w-lg mx-auto border border-white/20 overflow-hidden ${isClosing ? "modal-content-exit" : "modal-content-enter"
                  }`}
              >
                {/* Header */}
                <div className="flex items-center justify-between p-4 md:p-6 border-b border-white/20">
                  <div className="flex items-center gap-3">
                    <IoGameController className="text-white" size={24} />
                    <h2 className="text-xl md:text-2xl font-bold text-white tracking-wider">
                      Rate This Game
                    </h2>
                  </div>
                  <button
                    onClick={closeReviewModal}
                    className="text-gray-400 hover:text-white transition-colors p-2 rounded-lg hover:bg-white/10"
                  // disabled={isSubmitting}
                  >
                    <FaTimes size={20} />
                  </button>
                </div>

                {/* Content */}

                <form onSubmit={handleSubmit} className="p-4 md:p-6 space-y-6">
                  {/* Rating Stars */}
                  <div className="text-center space-y-3">
                    <label className="block text-white font-semibold text-lg tracking-wider">
                      Your Rating
                    </label>
                    <div className="flex justify-center items-center gap-2">
                      {renderStars()}
                    </div>
                  </div>

                  {/* Description */}
                  <div className="space-y-3">
                    <label
                      htmlFor="description"
                      className="block text-white font-semibold tracking-wider"
                    >
                      Review
                    </label>
                    <textarea
                      id="description"
                      value={values.description}
                      onChange={handleChange}
                      placeholder="Share your thoughts about this game..."
                      className="w-full px-4 py-3 bg-black/25 border border-white/20 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none transition-all duration-200 backdrop-blur-sm"
                      rows={4}
                      maxLength={500}
                    // disabled={isSubmitting}
                    />
                    <div className="text-right text-xs text-gray-400">
                      {values.description.length}/500 characters
                    </div>
                  </div>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={values.rate === 0}
                    className="w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 disabled:from-gray-600 disabled:to-gray-700 text-white font-semibold py-3 px-6 rounded-xl transition-all duration-200 transform hover:scale-105 disabled:scale-100 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-lg"
                  >
                    <BsSend size={18} />
                    {reviewData ? "Edit Review" : "Submit Review"}
                  </button>
                </form>

                {/* Footer */}
                <div className="px-6 py-4 bg-white/5 border-t border-white/10">
                  <p className="text-xs text-gray-400 text-center">
                    Your review helps other gamers make better choices
                  </p>
                </div>
              </div>
            </div>
          )}

          <Modal open={delOpen} onClose={handleDeleteClose}>
            <Box className="bg-primary-dark text-white absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 p-4 rounded max-w-[500px] w-[100%] max-h-[90vh] overflow-y-auto">
              <div className="p-5">
                <div className="text-center">
                  <p className="text-brown font-bold text-xl">Delete Review</p>
                  <p className="text-brown-50 mt-2">
                    Are you sure you want to delete "{reviewData?.description}"?
                  </p>
                </div>
                <div className="flex flex-wrap gap-3 mt-6 justify-center">
                  <button
                    onClick={handleDeleteClose}
                    className="text-brown w-32 border-brown border px-4 py-2 rounded hover:bg-brown-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleDeleterate}
                    disabled={ratingLoadingStates.deleterating}
                    className="bg-brown text-white w-32 border-brown border px-4 py-2 rounded hover:bg-brown-50 disabled:opacity-50"
                  >
                    {ratingLoadingStates.deleterating ? "Deleting..." : "Delete"}
                  </button>
                </div>
              </div>
            </Box>
          </Modal>

          <ShareModal
            isOpen={showShareModal}
            onClose={() => setShowShareModal(false)}
            singleGame={singleGame}
            currentUrl={window.location.href}
          />

          <OrderSuccessModal
            open={showSuccessModal}
            onClose={() => {
              setShowSuccessModal(false);
              navigate('/profile/orders');
            }}
          />
        </div >
      )
      }
    </>
  );
}
